import { FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { SmsTemplate } from './smsTemplateListFlowMapper';

type SmsTemplateTableProps = {
  items: SmsTemplate[];
  onSelectTemplate?: (template: SmsTemplate) => void;
};

export const SmsTemplateTable: FC<SmsTemplateTableProps> = ({ items, onSelectTemplate }) => {
  const { t } = useTranslation('campaign');
  const isClickable = Boolean(onSelectTemplate);

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, template: SmsTemplate) => {
    if (!onSelectTemplate) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectTemplate(template);
    }
  };

  return (
    <div className='overflow-hidden rounded-2xl border border-secondary-border bg-white'>
      <table className='min-w-full divide-y divide-secondary-border'>
        <thead className='bg-slate-50'>
          <tr>
            <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-quaternary-500'>
              {t('smsWorkspace.templates.tableName')}
            </th>
            <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-quaternary-500'>
              {t('smsWorkspace.templates.tablePreview')}
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-secondary-border'>
          {items.map((template) => (
            <tr
              key={template.id}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={isClickable ? () => onSelectTemplate?.(template) : undefined}
              onKeyDown={isClickable ? (event) => handleKeyDown(event, template) : undefined}
              className={twMerge(
                'outline-none transition',
                isClickable && 'cursor-pointer hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400'
              )}
            >
              <td className='px-4 py-3 text-sm font-semibold text-slate-900'>{template.name}</td>
              <td className='px-4 py-3 text-sm text-secondary-600'>
                <span className='line-clamp-2'>{template.previewText || template.id}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
